import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/auth-context';
import { AUTH_MESSAGES, CONSOLE_MESSAGES } from '../constants';
import { MODAL_CLASSES, SPINNER_CLASSES } from '../styles';

function VerifyEmailModal({ isOpen, onClose, token }) {
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [hasVerified, setHasVerified] = useState(false);
  const { verifyEmail: authVerifyEmail } = useAuth();

  useEffect(() => {
    const handleVerifyEmail = async (token) => {
      try {
        const result = await authVerifyEmail(token);
        
        if (result.success) {
          setStatus('success');
          setMessage(result.message || AUTH_MESSAGES.EMAIL_VERIFIED);
        } else {
          setStatus('error');
          setMessage(result.message || AUTH_MESSAGES.EMAIL_VERIFICATION_ERROR);
        }
      } catch (error) {
        console.error(CONSOLE_MESSAGES.EMAIL_VERIFICATION_ERROR, error);
        setStatus('error');
        setMessage(error.message || AUTH_MESSAGES.EMAIL_VERIFICATION_ERROR);
      }
    };

    if (isOpen && token && !hasVerified) {
      setStatus('verifying');
      setMessage('');
      setHasVerified(true);
      handleVerifyEmail(token);
    }
  }, [isOpen, token, hasVerified, authVerifyEmail]);

  // Handle modal close
  const handleClose = () => {
    setStatus('verifying');
    setMessage('');
    setHasVerified(false);
    onClose();
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={MODAL_CLASSES.OVERLAY}
      onClick={handleOverlayClick}
    >
      <div className="relative w-full max-w-md bg-dark-800 rounded-xl sm:rounded-2xl shadow-2xl border border-dark-700 overflow-hidden max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-1.5 sm:p-2 text-gray-400 hover:text-white transition-colors"
          aria-label="Uždaryti"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="p-4 sm:p-6 pb-3 sm:pb-4">
          <div className="text-center">
            <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 ${
              status === 'success' 
                ? 'bg-gradient-to-br from-green-500 to-green-700'
                : status === 'error'
                ? 'bg-gradient-to-br from-red-500 to-red-700'
                : 'bg-gradient-to-br from-primary-500 to-primary-700'
            }`}>
              {status === 'verifying' && (
                <div className={SPINNER_CLASSES.XLARGE_WHITE}></div>
              )}
              {status === 'success' && (
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              )}
              {status === 'error' && (
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1.5 sm:mb-2">
              {status === 'verifying' && 'Patvirtinamas el. paštas...'}
              {status === 'success' && 'El. paštas patvirtintas!'}
              {status === 'error' && 'Patvirtinimo klaida'}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 pb-4 sm:pb-6">
          <div className="space-y-4 sm:space-y-6">
            {status === 'verifying' && (
              <div className="text-center">
                <p className="text-gray-400 text-sm">
                  Prašome palaukti, kol patvirtinsime jūsų el. pašto adresą...
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="space-y-3 sm:space-y-4">
                <div className="p-3 sm:p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                  <p className="text-green-400 font-medium text-xs sm:text-sm">
                    {message}
                  </p>
                </div>
                
                <div className="text-center">
                  <p className="text-gray-300 mb-3 sm:mb-4 text-sm">
                    Jūsų paskyra dabar aktyvuota. Galite prisijungti ir pradėti naudotis FilmųDB!
                  </p>
                  
                  <ul className="text-gray-400 text-xs sm:text-sm space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
                    <li className="flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Kurkite filmų ir serialų sąrašus
                    </li>
                    <li className="flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Įvertinkite filmus ir serialus
                    </li>
                    <li className="flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Gaukite personalizuotus pasiūlymus
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-3 sm:space-y-4">
                <div className="p-3 sm:p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
                  <p className="text-red-400 text-xs sm:text-sm">
                    {message}
                  </p>
                </div>
                
                <div className="text-center">
                  <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4">
                    Galimi sprendimo būdai:
                  </p>
                  <ul className="text-gray-400 text-xs sm:text-sm space-y-1 text-left mb-4 sm:mb-6">
                    <li>• Patikrinkite, ar nuoroda pilna ir teisinga</li>
                    <li>• Patvirtinimo nuoroda galioja tik 24 valandas</li>
                    <li>• Prašykite naujos patvirtinimo nuorodos</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Action button */}
            <button
              onClick={status === 'success' ? () => window.location.reload() : handleClose}
              className="btn-primary w-full justify-center h-10 sm:h-11 text-sm sm:text-base"
            >
              {status === 'success' ? 'Tęsti' : 'Grįžti į pradinį puslapį'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmailModal; 